// src/components/Sidebar.jsx - COMPLETE FIXED VERSION
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Star, X, Menu, Building2, FileText, Receipt, CheckCircle, Megaphone, LifeBuoy, ScrollText } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext.js";
import { hasPermission } from "../utils/checkPermission.js";
import { BlockRoomModal, UnblockRoomModal } from "./RoomBlockingModals";
import thaparLogo from "../assets/thapar_logo.png";
import useSystemSettings from "../hooks/useSystemSettings";

export default function Sidebar({
  activeHostel,
  setActiveHostel,
  setActiveRoomRef,
  hostelData,
  activeTab,
  setActiveTab,
  isSidebarOpen = true, // ✅ Default to true if not provided
}) {
  const navigate = useNavigate();
  const { currentUser, loading } = useAuth();
  const { settings } = useSystemSettings();
  console.log('AUTH USER:', currentUser); // 🔍 DIAGNOSTIC LOG
  const [blockRoomModal, setBlockRoomModal] = useState(null);
  const [unblockRoomModal, setUnblockRoomModal] = useState(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const logoPublicPath = "https://ik.imagekit.io/7khjnlfow/email-assets/thapar_logo.png?updatedAt=1776888126772";
  const isEnquiry = activeTab === "Enquiry";

  // ✅ ROLE EXTRACTION
  const role = currentUser?.role || currentUser?.user?.role;
  const roleKey = String(role || "").toLowerCase();
  const isRestrictedRole = role === 'caretaker' || role === 'warden';
  const canUseBroadcastCenter =
    settings?.operations?.enableBroadcastCenter !== false &&
    ["admin", "manager", "adosa"].includes(roleKey);
  const canUseSupportRequests =
    settings?.operations?.enableGuestSupportPortal !== false &&
    (["admin", "manager", "adosa", "assistant", "caretaker", "warden", "co_warden"].includes(roleKey) || hasPermission(currentUser, "sidebar.hostels"));

  // ✅ PERMISSION CHECKS
  const canSeeAllHostels = hasPermission(currentUser, "sidebar.allHostels");
  const canSeeHostels = hasPermission(currentUser, "sidebar.hostels");
  const hasGuestRoomPermission = currentUser?.permissions?.guestRoom === true; // Check custom permission

  const assignedHostel =
    currentUser?.assignedHostel ||
    currentUser?.hostel ||
    null;

  // Helper: Extract hostel initial
  const extractInitial = (hostelName) => {
    const match = hostelName.match(/\(([^)]+)\)/);
    if (match && match[1]) {
      return match[1].trim().toUpperCase();
    }
    return hostelName.charAt(0).toUpperCase();
  };

  // ✅ SORTED HOSTEL NAMES
  const hostelNames = useMemo(() => {
    return Object.keys(hostelData || {}).sort((a, b) => {
      const initialA = extractInitial(a);
      const initialB = extractInitial(b);
      return initialA.localeCompare(initialB, undefined, { sensitivity: 'base', numeric: true });
    });
  }, [hostelData]);

  // ✅ VISIBLE HOSTELS (Role-based)
  const visibleHostels = useMemo(() => {
    console.log("🔍 Sidebar - Checking visible hostels:", {
      role,
      canSeeAllHostels,
      canSeeHostels,
      assignedHostel,
      availableHostels: Object.keys(hostelData || {})
    });

    if (canSeeAllHostels) {
      console.log("✅ Admin/Manager - showing all hostels:", hostelNames);
      return hostelNames; // admin + manager (already sorted by initial)
    } else if (canSeeHostels && assignedHostel) {
      // ✅ FIX: Don't check if hostel exists in data - just show it
      // The data will load, and if hostel doesn't exist, it will show empty
      console.log("✅ Caretaker/Warden - showing assigned hostel:", assignedHostel);
      return [assignedHostel];
    }
    
    console.warn("❌ No hostels visible - check permissions");
    return [];
  }, [canSeeAllHostels, canSeeHostels, assignedHostel, hostelData, hostelNames, role]);

  // Warn if restricted role has no hostel
  if (isRestrictedRole && !assignedHostel) {
    console.warn(`⚠️ ${role} has no assigned hostel`);
  }

  // Auto-select hostel (only for admins with single hostel)
  const didAutoSelect = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (activeTab !== "Home") return;

    if (
      !didAutoSelect.current &&
      visibleHostels.length === 1 &&
      canSeeAllHostels
    ) {
      didAutoSelect.current = true;
      setActiveHostel(visibleHostels[0]);
    }
  }, [loading, visibleHostels, canSeeAllHostels, activeTab, setActiveHostel]);

  // ✅ NAVIGATION HANDLER (closes mobile menu)
  const handleNavigation = (callback) => {
    callback();
    setIsMobileOpen(false);
  };

  // ✅ CLOSE MOBILE MENU ON OUTSIDE CLICK
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMobileOpen && !e.target.closest('.mobile-sidebar') && !e.target.closest('.mobile-menu-button')) {
        setIsMobileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileOpen]);

  // ✅ SIDEBAR CONTENT (reusable for desktop & mobile)
  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* LOGO */}
      <div className={`flex flex-col items-center gap-1 border-b border-slate-200 ${
        isMobile ? 'py-4' : 'py-6'
      }`}>
        <img
          src={logoPublicPath}
          alt="Logo"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = thaparLogo;
          }}
          className={`object-contain rounded-xl shadow-sm mb-2 ${
            isMobile ? 'w-32 h-16' : 'w-40 h-20'
          }`}
        />
        <p className="text-[11px] text-slate-500">Guest Room Booking System</p>
      </div>

      {/* NAVIGATION */}
      <nav
        className={`flex-1 overflow-y-auto px-3 pt-3 pb-4 space-y-2 ${
          isEnquiry ? "pointer-events-none opacity-60" : ""
        }`}
      >
        {roleKey === "admin" && (
          <motion.button
            whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
            onClick={() => handleNavigation(() => navigate("/audit-activity"))}
            className="relative flex w-full items-center gap-3 rounded-xl border border-transparent bg-white/30 px-3 py-2 text-left hover:bg-white/80"
          >
            <ScrollText className="h-4 w-4 text-slate-600" />
            <span className="text-sm">System Audit & Activity</span>
          </motion.button>
        )}
        {/* ✅ ALL HOSTELS BUTTON (Admin + Manager only) */}
        {canSeeAllHostels && (
          <motion.button
            whileHover={!isEnquiry ? { scale: 1.01 } : {}}
            whileTap={!isEnquiry ? { scale: 0.98 } : {}}
            onClick={() => handleNavigation(() => {
              setActiveHostel(null);
              setActiveRoomRef(null);
              setActiveTab("AllHostelsPortal");
            })}
            className={`
              relative group w-full text-left px-3 py-2 rounded-xl
              border bg-white/40 backdrop-blur-xl flex items-center gap-3
              ${
                activeTab === "AllHostelsPortal"
                  ? "border-red-500 shadow-md"
                  : "border-transparent hover:bg-white/80"
              }
            `}
          >
            <Building2 className="w-4 h-4 text-slate-600" />
            <span className="text-sm">All Hostels</span>
          </motion.button>
        )}

        {/* ✅ HOSTEL LIST (All roles see their visible hostels) */}
        {visibleHostels.map((hostelName) => {
          const isActive = activeHostel === hostelName;

          return (
            <motion.button
              key={hostelName}
              whileHover={!isEnquiry ? { scale: 1.01 } : {}}
              whileTap={!isEnquiry ? { scale: 0.98 } : {}}
              onClick={() => handleNavigation(() => {
                setActiveHostel(hostelName);
                setActiveRoomRef(null);
                setActiveTab((prev) =>
                  ["Defaulters", "Feedback", "DepartmentPayments", "Approvals", "Bills", "Bookings", "BroadcastCenter", "SupportRequests"].includes(prev) ? prev : "Home"
                );
              })}
              className={`
                relative group w-full text-left px-3 py-2 rounded-xl border
                bg-white/30 backdrop-blur-xl flex items-center gap-3
                ${
                  isActive
                    ? "border-red-500 shadow-md"
                    : "border-transparent hover:bg-white/80"
                }
              `}
            >
              <Building2 className="w-4 h-4 text-slate-600" />
              <span className={`text-sm ${isActive ? "font-semibold" : ""}`}>
                {hostelName}
              </span>
            </motion.button>
          );
        })}

        {/* ✅ DEFAULTERS BUTTON (Admin, Manager, Caretaker, Warden, Guest Room Permission) */}
        {(['admin', 'manager', 'caretaker', 'warden'].includes(role) || hasGuestRoomPermission) && (
          <motion.button
            whileHover={!isEnquiry ? { scale: 1.01 } : {}}
            whileTap={!isEnquiry ? { scale: 0.98 } : {}}
            onClick={() => handleNavigation(() => {
              console.log("🔴 Defaulters clicked");
              setActiveTab("Defaulters");
              setActiveHostel(null);
              setActiveRoomRef(null);
            })}
            className={`
              relative group w-full text-left px-3 py-2 rounded-xl border
              bg-white/30 backdrop-blur-xl flex items-center gap-3
              ${
                activeTab === "Defaulters"
                  ? "border-red-500 shadow-md"
                  : "border-transparent hover:bg-white/80"
              }
            `}
          >
            <AlertCircle className="w-4 h-4 text-slate-600" />
            <span className={`text-sm ${activeTab === "Defaulters" ? "font-semibold" : ""}`}>
              Defaulters
            </span>
          </motion.button>
        )}

        {/* ✅ DEPARTMENT PAYMENTS BUTTON (Admin, Manager, Caretaker, Warden, Guest Room Permission) */}
        {(['admin', 'manager', 'caretaker', 'warden'].includes(role) || hasGuestRoomPermission) && (
          <motion.button
            whileHover={!isEnquiry ? { scale: 1.01 } : {}}
            whileTap={!isEnquiry ? { scale: 0.98 } : {}}
            onClick={() => handleNavigation(() => {
              console.log("🏢 Department Payments clicked");
              setActiveTab("DepartmentPayments");
              setActiveHostel(null);
              setActiveRoomRef(null);
            })}
            className={`
              relative group w-full text-left px-3 py-2 rounded-xl border
              bg-white/30 backdrop-blur-xl flex items-center gap-3
              ${
                activeTab === "DepartmentPayments"
                  ? "border-red-500 shadow-md"
                  : "border-transparent hover:bg-white/80"
              }
            `}
          >
            <Building2 className="w-4 h-4 text-slate-600" />
            <span className={`text-sm ${activeTab === "DepartmentPayments" ? "font-semibold" : ""}`}>
              Dept. Payments
            </span>
          </motion.button>
        )}

        {/* ✅ BILLS BUTTON (Admin, Manager, ADOSA, Assistant, Caretaker, Warden, Co-Warden, Guest Room Permission) */}
        {(['admin', 'manager', 'adosa', 'assistant', 'caretaker', 'warden', 'co_warden'].includes(role) || hasGuestRoomPermission) && (
          <motion.button
            whileHover={!isEnquiry ? { scale: 1.01 } : {}}
            whileTap={!isEnquiry ? { scale: 0.98 } : {}}
            onClick={() => handleNavigation(() => {
              console.log("🧾 Bills clicked");
              setActiveTab("Bills");
              setActiveHostel(null);
              setActiveRoomRef(null);
            })}
            className={`
              relative group w-full text-left px-3 py-2 rounded-xl border
              bg-white/30 backdrop-blur-xl flex items-center gap-3
              ${
                activeTab === "Bills"
                  ? "border-red-500 shadow-md"
                  : "border-transparent hover:bg-white/80"
              }
            `}
          >
            <Receipt className="w-4 h-4 text-slate-600" />
            <span className={`text-sm ${activeTab === "Bills" ? "font-semibold" : ""}`}>
              Bills
            </span>
          </motion.button>
        )}

        {/* ✅ APPROVALS BUTTON (Admin, Manager, ADOSA, Assistant, Caretaker, Warden, Co-Warden, Guest Room Permission) */}
        {(['admin', 'manager', 'adosa', 'assistant', 'caretaker', 'warden', 'co_warden'].includes(role) || hasGuestRoomPermission) && (
          <motion.button
            whileHover={!isEnquiry ? { scale: 1.01 } : {}}
            whileTap={!isEnquiry ? { scale: 0.98 } : {}}
            onClick={() => handleNavigation(() => {
              console.log("✅ Approvals clicked");
              setActiveTab("Approvals");
              setActiveHostel(null);
              setActiveRoomRef(null);
            })}
            className={`
              relative group w-full text-left px-3 py-2 rounded-xl border
              bg-white/30 backdrop-blur-xl flex items-center gap-3
              ${
                activeTab === "Approvals"
                  ? "border-red-500 shadow-md"
                  : "border-transparent hover:bg-white/80"
              }
            `}
          >
            <CheckCircle className="w-4 h-4 text-slate-600" />
            <span className={`text-sm ${activeTab === "Approvals" ? "font-semibold" : ""}`}>
              Approvals
            </span>
          </motion.button>
        )}

        {/* ✅ FEEDBACK BUTTON (All roles) */}
        <motion.button
          whileHover={!isEnquiry ? { scale: 1.01 } : {}}
          whileTap={!isEnquiry ? { scale: 0.98 } : {}}
          onClick={(e) => handleNavigation(() => {
            e.stopPropagation();
            console.log("⭐ Feedback clicked");
            setActiveTab("Feedback");
            setActiveHostel(null);
            setActiveRoomRef(null);
          })}
          className={`
            relative group w-full text-left px-3 py-2 rounded-xl border
            bg-white/30 backdrop-blur-xl flex items-center gap-3
            ${
              activeTab === "Feedback"
                ? "border-red-500 shadow-md"
                : "border-transparent hover:bg-white/80"
            }
          `}
        >
          <Star className="w-4 h-4 text-slate-600" />
          <span className={`text-sm ${activeTab === "Feedback" ? "font-semibold" : ""}`}>
            Guest Feedback
          </span>
        </motion.button>

        {/* ✅ BOOKINGS BUTTON (Admin, Manager, ADOSA, Assistant, Caretaker, Warden, Co-Warden, Guest Room Permission) */}
        {(['admin', 'manager', 'adosa', 'assistant', 'caretaker', 'warden', 'co_warden'].includes(role) || hasGuestRoomPermission) && (
          <motion.button
          whileHover={!isEnquiry ? { scale: 1.01 } : {}}
          whileTap={!isEnquiry ? { scale: 0.98 } : {}}
          onClick={(e) => handleNavigation(() => {
            e.stopPropagation();
            console.log("📋 Bookings clicked");
            setActiveTab("Bookings");
            setActiveHostel(null);
            setActiveRoomRef(null);
          })}
          className={`
            relative group w-full text-left px-3 py-2 rounded-xl border
            bg-white/30 backdrop-blur-xl flex items-center gap-3
            ${
              activeTab === "Bookings"
                ? "border-red-500 shadow-md"
                : "border-transparent hover:bg-white/80"
            }
          `}
        >
          <FileText className="w-4 h-4 text-slate-600" />
          <span className={`text-sm ${activeTab === "Bookings" ? "font-semibold" : ""}`}>
            All Bookings
          </span>
        </motion.button>
        )}

        {/* ✅ BROADCAST CENTER BUTTON (Admin, Manager, DoSA only) */}
        {canUseBroadcastCenter && (
          <motion.button
            whileHover={!isEnquiry ? { scale: 1.01 } : {}}
            whileTap={!isEnquiry ? { scale: 0.98 } : {}}
            onClick={(e) => handleNavigation(() => {
              e.stopPropagation();
              navigate("/broadcast-center");
              setActiveTab("BroadcastCenter");
              setActiveHostel(null);
              setActiveRoomRef(null);
            })}
            className={`
              relative group w-full text-left px-3 py-2 rounded-xl border
              bg-white/30 backdrop-blur-xl flex items-center gap-3
              ${
                activeTab === "BroadcastCenter"
                  ? "border-red-500 shadow-md"
                  : "border-transparent hover:bg-white/80"
              }
            `}
          >
            <Megaphone className="w-4 h-4 text-slate-600" />
            <span className={`text-sm ${activeTab === "BroadcastCenter" ? "font-semibold" : ""}`}>
              Broadcast Center
            </span>
          </motion.button>
        )}

        {canUseSupportRequests && (
          <motion.button
            whileHover={!isEnquiry ? { scale: 1.01 } : {}}
            whileTap={!isEnquiry ? { scale: 0.98 } : {}}
            onClick={(e) => handleNavigation(() => {
              e.stopPropagation();
              navigate("/support-requests");
              setActiveTab("SupportRequests");
              setActiveHostel(null);
              setActiveRoomRef(null);
            })}
            className={`
              relative group w-full text-left px-3 py-2 rounded-xl border
              bg-white/30 backdrop-blur-xl flex items-center gap-3
              ${
                activeTab === "SupportRequests"
                  ? "border-red-500 shadow-md"
                  : "border-transparent hover:bg-white/80"
              }
            `}
          >
            <LifeBuoy className="w-4 h-4 text-slate-600" />
            <span className={`text-sm ${activeTab === "SupportRequests" ? "font-semibold" : ""}`}>
              Support Requests
            </span>
          </motion.button>
        )}
      </nav>

      {/* FOOTER */}
      <div className="px-4 py-3 border-t border-slate-200 text-center mt-auto">
        <div className="flex flex-col items-center gap-1">
          <p className="text-[10px] text-slate-500 font-medium">
            Created and Maintained by <span className="text-slate-700">Office of Student Affairs</span>
          </p>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* ✅ MOBILE MENU BUTTON */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="mobile-menu-button fixed top-4 left-4 z-50 lg:hidden p-2 bg-white rounded-lg shadow-lg border border-red-300 hover:bg-red-50 transition"
      >
        {isMobileOpen ? (
          <X className="w-6 h-6 text-red-600" />
        ) : (
          <Menu className="w-6 h-6 text-red-600" />
        )}
      </button>

      {/* ✅ DESKTOP SIDEBAR */}
      <motion.aside
        initial={{ x: -280, opacity: 0 }}
        animate={{ x: isSidebarOpen ? 0 : -280, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className="
          hidden lg:flex
          fixed top-0 left-0 h-full w-64 flex-col z-20
          bg-white/70 backdrop-blur-xl
          border-r-4 border-red-500
          shadow-[0_18px_45px_rgba(15,23,42,0.18)]
          rounded-r-3xl text-slate-800
        "
      >
        <SidebarContent />
      </motion.aside>

      {/* ✅ MOBILE SIDEBAR */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setIsMobileOpen(false)}
            />

            {/* Sidebar */}
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="
                mobile-sidebar
                fixed top-0 left-0 h-full w-64 flex flex-col z-50
                bg-white/95 backdrop-blur-xl
                border-r-4 border-red-500
                shadow-2xl text-slate-800
                lg:hidden
              "
            >
              <SidebarContent isMobile={true} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ✅ MODALS */}
      {blockRoomModal && (
        <BlockRoomModal
          hostelName={blockRoomModal.hostelName}
          roomNo={blockRoomModal.roomNo}
          onClose={() => setBlockRoomModal(null)}
          onSuccess={() => {
            setBlockRoomModal(null);
            if (typeof window.fetchLatestHostelData === "function") {
              window.fetchLatestHostelData();
            }
          }}
        />
      )}

      {unblockRoomModal && (
        <UnblockRoomModal
          hostelName={unblockRoomModal.hostelName}
          roomNo={unblockRoomModal.roomNo}
          blockInfo={unblockRoomModal.blockInfo}
          onClose={() => setUnblockRoomModal(null)}
          onSuccess={() => {
            setUnblockRoomModal(null);
            if (typeof window.fetchLatestHostelData === "function") {
              window.fetchLatestHostelData();
            }
          }}
        />
      )}
    </>
  );
}
